/** Per-activity drill-in: trial-by-trial results, drop-off, pre-fill. */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { formatShortDate, pickNextSession } from '../../lib/effectiveslp/domain';
import { DropoffChart } from './DropoffChart';

interface CommonWrongAnswer {
  value: string;
  count: number;
}

interface RollupItem {
  item_id: string;
  position: number;
  target_trials: number | null;
  trials_attempted: number;
  trials_correct: number;
  accuracy_pct: number;
  last_attempted_at: string | null;
  bail_trial_index: number | null;
  common_wrong_answers: CommonWrongAnswer[];
}

interface PracticeSession {
  id: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  trials_attempted: number | null;
  trials_correct: number | null;
  activities_completed: number | null;
  bail_trial_index: number | null;
  device_fingerprint_hash: string | null;
}

interface Attempt {
  id?: string;
  trial_index: number | null;
  attempted_at: string;
  is_correct: boolean | null;
  child_response: unknown;
  child_identifier_hash: string;
  response_latency_ms: number | null;
}

interface SessionRow {
  id: string;
  scheduled_at: string;
  completed_at: string | null;
  notes_text: string | null;
}

interface Props {
  title: string;
  item: RollupItem;
  studentId: string;
  sessions: PracticeSession[];
  onBack: () => void;
}

function childResponseText(raw: unknown): string {
  if (raw == null) return '(no response)';
  if (typeof raw === 'string') return raw.slice(0, 64);
  try {
    return JSON.stringify(raw).slice(0, 64);
  } catch {
    return '(unreadable)';
  }
}

function truncateHash(hash: string): string {
  return `${hash.slice(0, 8)}…`;
}

export function PracticeActivityDetail({
  title,
  item,
  studentId,
  sessions,
  onBack,
}: Props): ReactElement {
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [expandedTrials, setExpandedTrials] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data, error: attemptsErr } = await supabase
          .from('practice_attempts')
          .select(
            'trial_index, attempted_at, is_correct, child_response, child_identifier_hash, response_latency_ms',
          )
          .eq('practice_set_item_id', item.item_id)
          .order('attempted_at', { ascending: true });
        if (cancelled) return;
        if (attemptsErr) {
          setError(attemptsErr.message);
        } else {
          setAttempts((data ?? []) as Attempt[]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.item_id]);

  async function addToNextSessionNotes(): Promise<void> {
    setError(null);
    const { data: sessionsData, error: sessionsErr } = await supabase
      .from('sessions')
      .select('id, scheduled_at, completed_at, notes_text')
      .eq('student_id', studentId)
      .order('scheduled_at', { ascending: false });
    if (sessionsErr) {
      setError(sessionsErr.message);
      return;
    }
    const rows = (sessionsData ?? []) as SessionRow[];
    const now = Date.now();
    const nextSession = pickNextSession(rows, now);
    if (!nextSession) {
      setError('No upcoming session found to pre-fill.');
      return;
    }

    const summary =
      `Practice summary (${formatShortDate(new Date().toISOString())}): ` +
      `${item.trials_attempted} trials, ${item.accuracy_pct}% accuracy. ` +
      (item.common_wrong_answers[0]
        ? `Common error: ${item.common_wrong_answers[0].value}. `
        : '') +
      (item.bail_trial_index != null
        ? `Drop-off observed around trial ${item.bail_trial_index + 1}.`
        : '');

    if (nextSession.notes_text && nextSession.notes_text.trim().length > 0) {
      setConfirmOpen(true);
      return;
    }

    const { error: updateErr } = await supabase
      .from('sessions')
      .update({ notes_text: summary })
      .eq('id', nextSession.id);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    setPrefilled(true);
  }

  async function confirmReplace(): Promise<void> {
    setConfirmOpen(false);
    const { data: sessionsData, error: sessionsErr } = await supabase
      .from('sessions')
      .select('id, scheduled_at, completed_at, notes_text')
      .eq('student_id', studentId)
      .order('scheduled_at', { ascending: false });
    if (sessionsErr) {
      setError(sessionsErr.message);
      return;
    }
    const rows = (sessionsData ?? []) as SessionRow[];
    const now = Date.now();
    const nextSession = pickNextSession(rows, now);
    if (!nextSession) return;

    const summary =
      `Practice summary (${formatShortDate(new Date().toISOString())}): ` +
      `${item.trials_attempted} trials, ${item.accuracy_pct}% accuracy. ` +
      (item.common_wrong_answers[0]
        ? `Common error: ${item.common_wrong_answers[0].value}. `
        : '') +
      (item.bail_trial_index != null
        ? `Drop-off observed around trial ${item.bail_trial_index + 1}.`
        : '');

    const { error: updateErr } = await supabase
      .from('sessions')
      .update({ notes_text: summary })
      .eq('id', nextSession.id);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    setPrefilled(true);
  }

  const dropOffSession = sessions.find((s) => s.bail_trial_index != null);
  const dropOffTrial = item.bail_trial_index;

  return (
    <section className="screen">
      <button type="button" className="link-button" onClick={onBack}>
        ← Back
      </button>
      <h2>{title}</h2>
      {error && <p className="error">{error}</p>}
      {prefilled && (
        <p className="tagline">Pre-filled the next session notes.</p>
      )}

      <div className="card stack-md">
        <button
          type="button"
          className="snap-button"
          onClick={() => void addToNextSessionNotes()}
        >
          Add to next session notes
        </button>
        <span className="tagline">
          {item.trials_attempted} trials · {item.accuracy_pct}% accuracy · Target{' '}
          {item.target_trials ?? '—'}
        </span>
      </div>

      <h3>Drop-off chart</h3>
      <div className="card">
        {sessions.length === 0 && dropOffTrial == null ? (
          <p>No drop-off recorded.</p>
        ) : (
          <>
            <DropoffChart sessions={sessions} emptyLabel="No drop-off recorded." />
            {dropOffTrial != null && dropOffSession && (
              <p className="tagline">
                Most recent drop-off: trial {dropOffTrial + 1} on{' '}
                {formatShortDate(dropOffSession.started_at)}.
              </p>
            )}
          </>
        )}
      </div>

      <h3>Common wrong answers</h3>
      {item.common_wrong_answers.length === 0 ? (
        <p className="tagline">No wrong answers recorded.</p>
      ) : (
        <ol>
          {item.common_wrong_answers
            .slice(0, 3)
            .map((answer) => (
              <li key={answer.value}>
                {answer.value}: {answer.count}
              </li>
            ))}
        </ol>
      )}

      <h3>Trial history</h3>
      {loading ? (
        <p className="tagline">Loading trials…</p>
      ) : attempts.length === 0 ? (
        <p className="tagline">No individual trial data available.</p>
      ) : (
        <div className="stack-md">
          {attempts.map((trial, index) => {
            const trialNumber = trial.trial_index ?? index + 1;
            const isExpanded = expandedTrials.has(index);
            return (
              <details
                key={index}
                open={isExpanded}
                onToggle={() =>
                  setExpandedTrials((prev) => {
                    const next = new Set(prev);
                    if (next.has(index)) next.delete(index);
                    else next.add(index);
                    return next;
                  })
                }
              >
                <summary>
                  Trial {trialNumber} ·{' '}
                  {trial.is_correct === true
                    ? 'Correct'
                    : trial.is_correct === false
                      ? 'Incorrect'
                      : 'Unscored'}{' '}
                  · {formatShortDate(trial.attempted_at)}
                </summary>
                <div className="card">
                  <p>Response: {childResponseText(trial.child_response)}</p>
                  <p>Child: {truncateHash(trial.child_identifier_hash)}</p>
                  {trial.response_latency_ms != null && (
                    <p>Latency: {trial.response_latency_ms} ms</p>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      )}

      {confirmOpen && (
        <div className="card stack-md">
          <p>
            Replace current notes with the practice summary?
          </p>
          <div className="button-row">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="snap-button"
              onClick={() => void confirmReplace()}
            >
              Replace
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default PracticeActivityDetail;
